/**
 * "If a form is SKIPPED, any dependency on the form as a whole or on each of
 * its fields is ignored" (z8nrz7fn5r, client spec "Skip Form").
 *
 * A later form reads an earlier one through cross-form refs (`{handle.fieldId}`)
 * in visibility rules and in its gates (unlock, signature, completion). When the
 * form behind a ref was skipped, the rule or gate is IGNORED, as if it were not
 * there: the field shows as it would with no rule, and the gate passes. Read as
 * empty instead, each gate failed closed and the later form could never be
 * completed, signed or, for a required field behind an unlock gate, filled.
 * Mirrors the server's form-engine/skipped-sources.ts, which enforces the same.
 *
 * `skipped` holds the HANDLES of the run's skipped forms.
 */
import type { FieldDefinition, FormDefinition, FormulaGate, VisibilityRule } from '@/types/cgt'
import { normalizeCrossFormRefSyntax, parseCrossFormRef } from '@/lib/crossForm'
import { extractFormulaRefs } from '@/lib/formula'
import { visibilitySourceIds } from '@/lib/formSections'

/** Whether a ref token (`handle.fieldId`) names a field of a skipped form. */
export function refIsSkipped(ref: string | undefined, skipped: ReadonlySet<string>): boolean {
  if (!ref || skipped.size === 0) return false
  const parsed = parseCrossFormRef(ref)
  return !!parsed && skipped.has(parsed.handle)
}

/** Whether a formula reads any field of a skipped form. */
export function formulaReadsSkipped(formula: string | undefined, skipped: ReadonlySet<string>): boolean {
  if (!formula || skipped.size === 0) return false
  return extractFormulaRefs(normalizeCrossFormRefSyntax(formula)).some(ref => refIsSkipped(ref, skipped))
}

/** Whether a visibility rule reads any field of a skipped form. */
export function ruleReadsSkipped(rule: VisibilityRule | undefined, skipped: ReadonlySet<string>): boolean {
  if (!rule || skipped.size === 0) return false
  return visibilitySourceIds(rule).some(ref => refIsSkipped(ref, skipped))
}

const keepGate = (gate: FormulaGate | undefined, skipped: ReadonlySet<string>) =>
  gate && formulaReadsSkipped(gate.formula, skipped) ? undefined : gate

const keepRule = (rule: VisibilityRule | undefined, skipped: ReadonlySet<string>) =>
  ruleReadsSkipped(rule, skipped) ? undefined : rule

function stripField(field: FieldDefinition, skipped: ReadonlySet<string>): FieldDefinition {
  const visibility = keepRule(field.visibility, skipped)
  const unlockGate = keepGate(field.unlockGate, skipped)
  const signatureGate = keepGate(field.signatureGate, skipped)
  if (visibility === field.visibility && unlockGate === field.unlockGate && signatureGate === field.signatureGate) {
    return field
  }
  return { ...field, visibility, unlockGate, signatureGate }
}

type SectionLike = { fields?: FieldDefinition[]; visibility?: VisibilityRule }
const isSectionLike = (x: unknown): x is SectionLike =>
  typeof x === 'object' && x !== null && Array.isArray((x as SectionLike).fields)

function stripList(list: unknown[], skipped: ReadonlySet<string>): unknown[] {
  return list.map(item =>
    isSectionLike(item)
      ? {
          ...item,
          visibility: keepRule(item.visibility, skipped),
          fields: (item.fields ?? []).map(f => stripField(f, skipped)),
        }
      : stripField(item as FieldDefinition, skipped),
  )
}

/**
 * The definition as the form behaves with the run's skipped forms ignored: every
 * visibility rule and gate that reads one is dropped. Shape-preserving (sections
 * stay under `sections` or `fields`, wherever they came), and the same object
 * when nothing reads a skipped form.
 */
export function withoutSkippedDependencies<T extends FormDefinition>(definition: T, skipped: ReadonlySet<string>): T {
  if (skipped.size === 0) return definition
  const raw = definition as T & { fields?: unknown[] }
  return {
    ...definition,
    ...(Array.isArray(raw.sections) ? { sections: stripList(raw.sections, skipped) } : {}),
    ...(Array.isArray(raw.fields) ? { fields: stripList(raw.fields, skipped) } : {}),
    completionGate: keepGate(definition.completionGate ?? undefined, skipped),
  }
}
